import { useTranslation } from 'react-i18next'
import { TEMPLATE_IDS, TemplateId } from '../../config'
import { LAYOUT_COLUMNS } from '../../schema/document'
import { TEMPLATES } from '../../templates'
import styles from './TemplatePicker.module.scss'

interface TemplatePickerProps {
  value: TemplateId
  onChange: (id: TemplateId) => void
}

/**
 * Choosing a template by looking at it.
 *
 * Each card is the template's own sketch: its rows, top to bottom, drawn as
 * blocks in the tile's own colour, with the photo rows darker than the text
 * rows. A picture of where the photos go, which is what the three differ in,
 * and no thumbnail file to keep in step with the code.
 */
function TemplatePicker({ value, onChange }: TemplatePickerProps) {
  const { t } = useTranslation()

  return (
    <div
      className={styles.grid}
      role="group"
      aria-label={t('WEBSITE.TEMPLATE.PICK')}
    >
      {TEMPLATE_IDS.map(id => {
        const selected = id === value
        return (
          <button
            key={id}
            type="button"
            className={`${styles.card} ${selected ? styles.cardSelected : ''}`}
            aria-pressed={selected}
            onClick={() => onChange(id)}
          >
            <span className={styles.sketch} aria-hidden="true">
              {TEMPLATES[id].sketch.map((line, index) => (
                <span
                  // The sketch's rows are fixed and never reordered, so their
                  // position is the only identity they have.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={`${styles.sketchRow} ${styles[`tone-${line.tone}`]}`}
                >
                  {LAYOUT_COLUMNS[line.layout].map((fraction, cell) => (
                    <span
                      // eslint-disable-next-line react/no-array-index-key
                      key={cell}
                      className={styles.sketchCell}
                      style={{ flexGrow: fraction }}
                    />
                  ))}
                </span>
              ))}
            </span>
            <span className={`label-bold-medium ${styles.name}`}>
              {t(`WEBSITE.TEMPLATES.${id}.NAME`)}
            </span>
            <span className={`body-small ${styles.description}`}>
              {t(`WEBSITE.TEMPLATES.${id}.DESCRIPTION`)}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default TemplatePicker
